import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { ProductCard } from "@/components/ProductCard";
import { ProductListCard } from "@/components/ProductListCard";
import { Input } from "@/components/ui/input";
import { Search, LayoutGrid, List, ChevronLeft, Leaf, Flame, ArrowUpDown } from "lucide-react";

const VIEW_KEY = "hyd_view_pref";

export default function CategoryPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [category, setCategory] = useState(null);
  const [allCats, setAllCats] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState("all");
  const [sort, setSort] = useState("popular"); // popular|price_asc|price_desc|new
  const [view, setView] = useState(localStorage.getItem(VIEW_KEY) || "grid"); // grid | list

  useEffect(() => {
    localStorage.setItem(VIEW_KEY, view);
  }, [view]);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const cats = await api.get("/categories");
        setAllCats(cats.data);
        const cat = cats.data.find((c) => c.slug === slug);
        setCategory(cat || null);
        if (cat) {
          const { data } = await api.get("/products", { params: { category_id: cat.id, limit: 200 } });
          setProducts(data);
        } else {
          setProducts([]);
        }
      } finally {
        setLoading(false);
      }
    })();
  }, [slug]);

  const filtered = useMemo(() => {
    let list = [...products];
    if (query) list = list.filter((p) => p.name.toLowerCase().includes(query.toLowerCase()));
    if (filter === "bestseller") list = list.filter((p) => p.is_bestseller);
    if (filter === "new") list = list.filter((p) => p.is_new);
    if (filter === "green") list = list.filter((p) => p.is_green_cracker);
    if (sort === "price_asc") list.sort((a, b) => a.price - b.price);
    else if (sort === "price_desc") list.sort((a, b) => b.price - a.price);
    else if (sort === "new") list.sort((a, b) => (b.is_new ? 1 : 0) - (a.is_new ? 1 : 0));
    else list.sort((a, b) => (b.is_bestseller ? 1 : 0) - (a.is_bestseller ? 1 : 0));
    return list;
  }, [products, query, filter, sort]);

  if (loading) return <div className="py-12 text-center text-hyd-text2">Loading...</div>;
  if (!category) {
    return (
      <div className="max-w-md mx-auto px-4 py-16 text-center">
        <p className="text-hyd-text2">Category not found.</p>
        <Link to="/" className="text-hyd-gold underline mt-2 inline-block">Back home</Link>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 py-4">
      {/* Breadcrumb + heading */}
      <button onClick={() => navigate(-1)} className="text-xs uppercase text-hyd-text2 hover:text-hyd-gold inline-flex items-center gap-1">
        <ChevronLeft className="w-3 h-3" /> All categories
      </button>

      {/* Sticky category switcher — visible on every category page so users can jump between categories */}
      <div className="mt-3 -mx-4 px-4 py-2 overflow-x-auto flex gap-2 fade-right-mask border-b border-hyd-border">
        {allCats.map((c) => {
          const active = c.slug === slug;
          return (
            <Link
              key={c.id}
              to={`/category/${c.slug}`}
              data-testid={`cat-chip-${c.slug}`}
              className={`shrink-0 flex items-center gap-2 pl-1 pr-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider border transition-colors ${
                active
                  ? "bg-hyd-gold text-hyd-bg border-hyd-gold shadow-glow-gold"
                  : "bg-hyd-panel border-hyd-border text-hyd-text2 hover:text-hyd-gold hover:border-hyd-gold/40"
              }`}
            >
              {c.image_url ? (
                <img src={c.image_url} alt="" className="w-6 h-6 rounded-full object-cover border border-hyd-border" />
              ) : null}
              <span>{c.name}</span>
            </Link>
          );
        })}
      </div>

      <div className="mt-3 flex items-center gap-4">
        {category.image_url && (
          <img src={category.image_url} alt="" className="w-14 h-14 rounded-2xl object-cover border border-hyd-border" />
        )}
        <div>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold">{category.name}</h1>
          <p className="text-hyd-text2 text-xs sm:text-sm">{products.length} items</p>
        </div>
      </div>

      {/* Search — sticky, gold-bordered */}
      <div className="sticky top-[64px] z-30 -mx-4 px-4 py-3 mt-4 bg-hyd-bg/90 backdrop-blur-md border-b border-hyd-border">
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-hyd-gold" />
          <Input
            placeholder={`Search in ${category.name}…`}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="pl-9 h-11 bg-hyd-panel border-2 border-hyd-gold text-hyd-text placeholder:text-hyd-text3 rounded-2xl shadow-glow-gold focus-visible:ring-hyd-gold"
          />
        </div>

        {/* Controls row */}
        <div className="mt-3 flex items-center gap-2 overflow-x-auto">
          <div className="flex bg-hyd-panel border border-hyd-border rounded-full overflow-hidden shrink-0">
            <button
              onClick={() => setView("grid")}
              className={`px-3 py-1.5 text-xs uppercase tracking-widest font-bold flex items-center gap-1 ${view === "grid" ? "bg-hyd-gold text-hyd-bg" : "text-hyd-text2 hover:text-hyd-gold"}`}
              data-testid="category-view-grid"
              aria-label="Grid view"
            >
              <LayoutGrid className="w-3.5 h-3.5" /> Grid
            </button>
            <button
              onClick={() => setView("list")}
              className={`px-3 py-1.5 text-xs uppercase tracking-widest font-bold flex items-center gap-1 ${view === "list" ? "bg-hyd-gold text-hyd-bg" : "text-hyd-text2 hover:text-hyd-gold"}`}
              data-testid="category-view-list"
              aria-label="List view"
            >
              <List className="w-3.5 h-3.5" /> List
            </button>
          </div>

          {[
            { key: "all", label: "All" },
            { key: "bestseller", label: "Bestseller", icon: Flame },
            { key: "new", label: "New" },
            { key: "green", label: "Green", icon: Leaf },
          ].map((c) => (
            <button
              key={c.key}
              onClick={() => setFilter(c.key)}
              className={`shrink-0 px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider ${filter === c.key ? "bg-hyd-red text-white" : "bg-hyd-panel border border-hyd-border text-hyd-text2 hover:text-hyd-gold"}`}
            >
              {c.icon ? <c.icon className="w-3 h-3 mr-1 inline" /> : null}{c.label}
            </button>
          ))}

          <div className="ml-auto shrink-0 flex items-center gap-1 text-xs bg-hyd-panel border border-hyd-border rounded-full px-3 py-1.5 text-hyd-text2">
            <ArrowUpDown className="w-3 h-3 text-hyd-gold" />
            <select value={sort} onChange={(e) => setSort(e.target.value)} className="bg-transparent outline-none text-hyd-text">
              <option value="popular">Popular</option>
              <option value="price_asc">Price: Low → High</option>
              <option value="price_desc">Price: High → Low</option>
              <option value="new">New arrivals</option>
            </select>
          </div>
        </div>
      </div>

      {/* Products */}
      {filtered.length === 0 ? (
        <div className="py-16 text-center text-hyd-text2">No products match your filters.</div>
      ) : view === "grid" ? (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4">
          {filtered.map((p) => <ProductCard key={p.id} product={p} />)}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-4">
          {filtered.map((p) => <ProductListCard key={p.id} product={p} />)}
        </div>
      )}

      {/* Other categories */}
      <section className="mt-10">
        <h2 className="font-display text-xl font-bold mb-3">Explore other categories</h2>
        <div className="grid grid-cols-3 sm:grid-cols-6 gap-3">
          {allCats.filter((c) => c.slug !== slug).slice(0, 6).map((c) => (
            <Link key={c.id} to={`/category/${c.slug}`} data-testid={`cat-link-${c.slug}`} className="rounded-2xl overflow-hidden border border-hyd-border bg-hyd-panel hover:border-hyd-gold transition-colors">
              <div className="aspect-square bg-hyd-panel2">
                {c.image_url && <img src={c.image_url} alt="" className="w-full h-full object-cover" />}
              </div>
              <div className="p-2 text-center text-xs font-bold text-hyd-text2 truncate">{c.name}</div>
            </Link>
          ))}
        </div>
      </section>
    </div>
  );
}
